/** Compose a backend image `{p, s}` ref into a displayable URL, optionally at a
 *  resize preset.
 *
 * The image resize service (AWS API-Gateway + Lambda at `VITE_AWS_IMAGE_BASE_URL`,
 * e.g. `https://images.curatedplanet.com/s/`) serves on-the-fly thumbnails. The
 * URL is the key path — `p` (prefix), an optional resize `preset`, then `s`
 * (suffix) — joined with `/` and then FULLY `%2f`-encoded into a single segment.
 * This mirrors the dashboard's `images.js makeURL` exactly, so previews never
 * download the original.
 *
 * `imageUrl(ref)` (no preset) yields the original; `imageUrl(ref, IMAGE_PRESET.THUMB)`
 * a ~175px thumbnail. Returns null when the ref is absent or the base is not
 * configured, so callers degrade to a placeholder.
 */
import { config } from '@/config';

export interface ImageChunks {
  p: string;
  s: string;
}

/** Resize presets — path segments the resizer understands (`{fit}_{w}x{h}`). */
export const IMAGE_PRESET = {
  THUMB: 'cover_175x175', // gallery grid cell
  DETAIL: 'cover_350x200', // lightbox / detail preview
} as const;
export type ImagePreset = (typeof IMAGE_PRESET)[keyof typeof IMAGE_PRESET];

export function imageUrl(ref: ImageChunks | null | undefined, preset?: ImagePreset | string): string | null {
  if (!ref || !config.awsImageBaseUrl) return null;
  const chunks = preset ? [ref.p, preset, ref.s] : [ref.p, ref.s];
  return `${config.awsImageBaseUrl}${chunks.join('/').replace(/\//g, '%2f')}`;
}

/** The `{p, s}` ref of an image from its media-CDN URL (a staged upload's `url`): the
 *  URL's path IS the image's storage key (`Image.key_chunks`: the S3 path less the bucket,
 *  which the CDN host stands in for), each segment unquoted as the backend does
 *  (`unquote_plus`). Null when the URL has no file segment or cannot be parsed. */
export function imageChunksFromUrl(url: string | null | undefined): ImageChunks | null {
  if (!url) return null;
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    return null;
  }
  let chunks: string[];
  try {
    chunks = path
      .split('/')
      .filter(Boolean)
      .map((c) => decodeURIComponent(c.replace(/\+/g, ' ')));
  } catch {
    return null;
  }
  const s = chunks.pop();
  return s ? { p: chunks.join('/'), s } : null;
}
